// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTimeZone } from "@sequentech/ui-core";
import { colonHex } from "@/lib/signing/der";
import { DocumentKind, SigningAction } from "@/lib/signing/types";
import { toDate as parse } from "@/lib/timezones/zonedFormat";
import { useZonedFormat } from "@/hooks/useZonedFormat";
var formatter = function (locale, options, timeZone) {
    try {
        return new Intl.DateTimeFormat(locale, __assign(__assign({}, options), { timeZone: timeZone || undefined }));
    }
    catch (_a) {
        // An unknown zone name: the browser's own zone.
        return new Intl.DateTimeFormat(locale, options);
    }
};
/** A 24-hour time with the localized zone label, as signers compare times. */
export var formatTime = function (value, locale, timeZone, t) {
    var date = parse(value);
    return date
        ? formatDateTimeZone(date, timeZone, {
            t: t,
            lang: locale,
            formatDateTime: function (instant, zone) {
                return formatter(locale, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }, zone).format(instant);
            },
        })
        : "";
};
/** "Jan 11, 2030" in English. */
export var formatDate = function (value, locale, timeZone) {
    var date = parse(value);
    return date
        ? formatter(locale, { day: "numeric", month: "short", year: "numeric" }, timeZone).format(date)
        : "";
};
/** The date and 24-hour time with a localized timezone label. */
export var formatDateTime = function (value, locale, timeZone, t) {
    var date = parse(value);
    return date
        ? formatDateTimeZone(date, timeZone, {
            t: t,
            lang: locale,
            formatDateTime: function (instant, zone) {
                return formatter(locale, {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    hourCycle: "h23",
                }, zone).format(instant);
            },
        })
        : "";
};
/** Times and dates in the election event's zone (the panel's `time_zone`), else the browser's. */
export var useSigningFormat = function (timeZone) {
    var _a = useTranslation(), t = _a.t, i18n = _a.i18n;
    var locale = i18n.language;
    var zoned = useZonedFormat(timeZone);
    return useMemo(function () { return (__assign(__assign({}, zoned), { time: function (value) {
            return formatTime(value, locale, zoned.zone, t);
        }, date: function (value) { return formatDate(value, locale, timeZone); }, dateTime: function (value) {
            return formatDateTime(value, locale, zoned.zone, t);
        } })); }, [zoned, locale, timeZone, t]);
};
/**
 * The organization's name in copy: the tenant's display name
 * (`settings.display_name`), else its slug, which the menu shows.
 */
export var organizationName = function (tenant) {
    var _a;
    var displayName = (_a = tenant === null || tenant === void 0 ? void 0 : tenant.settings) === null || _a === void 0 ? void 0 : _a.display_name;
    if (typeof displayName === "string" && displayName.trim()) {
        return displayName.trim();
    }
    return (tenant === null || tenant === void 0 ? void 0 : tenant.slug) || null;
};
/** "Jose R. Dela Cruz and Ana P. Reyes". */
export var formatList = function (names, locale) {
    return new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(names);
};
/** "7f3a91c2…0de04b": enough of a SHA-256 to compare by eye. */
export var shortHash = function (lowerHex) {
    return lowerHex.length > 16 ? "".concat(lowerHex.slice(0, 8), "\u2026").concat(lowerHex.slice(-6)) : lowerHex;
};
/** "F7:08:0F:24:0A:58:E1:B6…D6:FE:78". */
export var shortFingerprint = function (lowerHex) {
    var full = colonHex(lowerHex);
    return full.length > 36 ? "".concat(full.slice(0, 23), "\u2026").concat(full.slice(-8)) : full;
};
export var documentTypeLabel = function (kind) {
    return kind === DocumentKind.Eml ? "EML" : "PDF";
};
/** The action's short name; a configuration version's with its number. */
var actionTitle = function (t, data) {
    return data.request.action === SigningAction.ApproveConfiguration && data.request.config_revision
        ? t("signing.panel.configurationVersion", { version: data.request.config_revision })
        : t("signing.actions.".concat(data.request.action, ".short"));
};
/**
 * "Election returns · Madrid PE · Spain": the action, then the Post and the
 * country; "Configuration version 18" for the version a request publishes.
 */
export var requestTitle = function (t, data) {
    return [actionTitle(t, data), data.election_name, data.area_name]
        .filter(function (part) { return !!part; })
        .join(" · ");
};
export var actionObject = function (t, action) {
    return t("signing.actions.".concat(action, ".object"));
};
/**
 * What an action does. Close voting at an event that seals its ballot boxes
 * at close (VOTE-FREEZE) says that it seals them.
 */
export var actionDescription = function (t, action, sealsBallots) {
    return t("signing.actions.".concat(action, ".").concat(sealsBallots && action === SigningAction.CloseVoting
        ? "descriptionSealed"
        : "description"));
};
